import Box from "@mui/material/Box";
import { useTranslationApp } from "../../../../../lib/i18next";
import { useSelectorApp } from "../../../../../lib/redux";
import Select from "../../../../../components/Form/Select";
import SelectBusinessAccount from "../../../../../components/Meta/SelectBusinessAccount";
import SelectPageFacebook from "../../../../../components/Meta/SelectPageFacebook";
import SelectInstagramAccount from "../../../../../components/Meta/SelectInstagramAccount";
import SelectDestinationTypeMeta from "../../../../../components/Meta/SelectDestinationTypeMeta";
import SelectCatalogMeta from "../../../../../components/Platforms/Meta/Campaign/SelectCatalogMeta";
import SelectAdvertisableAppsMeta from "../../../../../components/Platforms/Meta/Ad/SelectAdvertisableAppsMeta";
import SelectTrackingIDField from "../../../../../components/Form/SelectTrackingIDField";
import Typography from "@mui/material/Typography";
import {
  optionsAdsCampaignObjectivesMeta,
  optionsAppPromotion,
} from "../../../../../utils/options";
import {
  ADS_CAMPAIGN_APP_PROMOTION_IOS_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  META,
} from "../../../../../utils/constants";
import {
  AD_ACCOUNT_META_PLACEHOLDER,
  CATALOG_PLACEHOLDER,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_APP_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_APP_PROMOTION_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_CATALOG_FOR_APP_INSTALL_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_CATALOG_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_DESTINATION_TYPE_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_IDENTITIES_ACCOUNTS_LABEL,
  CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_OBJECTIVE_LABEL,
  INSTAGRAM_ACCOUNT_PLACEHOLDER,
  OPTIONAL,
  PAGE_FACEBOOK_PLACEHOLDER,
  SELECT_A_OPTION,
  TRACKING_META_ID_PLACEHOLDER,
} from "../../../../../i18n/keysTranslations";
import { hasInstagramAccountsConnected } from "../../../../../utils/meta";
import { getMetaDefaultsFieldVisibility } from "../../../../../utils/platformDefaults/platformDefaultsFieldVisibility";
import { PLATFORM_DEFAULTS_FIELD_LABEL_SX } from "../../../../../utils/platformDefaults/platformDefaultsFieldLabelSx";
import { INPUT_COLOR_BORDER } from "../../../../../utils/colors";

const PlatformDefaultsFormMeta = ({ formMethods }) => {
  const { t } = useTranslationApp();
  const { integrations } = useSelectorApp((state) => state.user);
  const { control, setValue, watch } = formMethods;

  const objective = watch("objective");
  const destinationType = watch("destinationType");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const pageID = watch("pageID");

  const visibility = getMetaDefaultsFieldVisibility({
    objective,
    destinationType,
  });

  const hasInstagramAccounts = hasInstagramAccountsConnected(
    integrations,
    integrationID,
    pageID
  );
  const isInstagramRequired =
    destinationType &&
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_IG_REQUIRED.includes(
      destinationType
    );
  const instagramLabel =
    hasInstagramAccounts || isInstagramRequired
      ? t(INSTAGRAM_ACCOUNT_PLACEHOLDER)
      : `${t(INSTAGRAM_ACCOUNT_PLACEHOLDER)} (${t(OPTIONAL)})`;

  const catalogTitleKey =
    objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION
      ? CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_CATALOG_FOR_APP_INSTALL_LABEL
      : CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_CATALOG_LABEL;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {visibility.objective ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_OBJECTIVE_LABEL)}
          </Typography>
          <Select
            options={optionsAdsCampaignObjectivesMeta}
            name="objective"
            variant="filled"
            fullWidth
            label={t(SELECT_A_OPTION)}
            doTranslate={true}
            control={control}
            actionsChange={() => {
              setValue(
                "destinationType",
                ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION
              );
              setValue("catalogID", "");
            }}
          />
        </Box>
      ) : null}

      {visibility.destinationType ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_DESTINATION_TYPE_LABEL)}
          </Typography>
          <SelectDestinationTypeMeta
            control={control}
            name="destinationType"
            fullWidth
            variant="filled"
            placeholder={SELECT_A_OPTION}
            objective={objective}
            actionsChange={() => {
              setValue("catalogID", "");
              setValue("useFBPageAsIG", false);
            }}
            from="campaign"
          />
        </Box>
      ) : null}

      {visibility.appPromotion ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_APP_PROMOTION_LABEL)}
          </Typography>
          <Select
            options={optionsAppPromotion}
            name="appPromotion"
            variant="filled"
            fullWidth
            label={t(SELECT_A_OPTION)}
            doTranslate={true}
            control={control}
            actionsChange={(value) => {
              setValue(
                "isIOS14Campaign",
                value === ADS_CAMPAIGN_APP_PROMOTION_IOS_OPTION
              );
            }}
          />
        </Box>
      ) : null}

      {visibility.identities ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_IDENTITIES_ACCOUNTS_LABEL)}
          </Typography>
          <SelectBusinessAccount
            variant="filled"
            label={t(AD_ACCOUNT_META_PLACEHOLDER)}
            control={control}
            platform={META}
            name="adAccountID"
            onChangeAction={(value) => {
              setValue("integrationID", value?.integrationID);
              setValue("pageID", "");
              setValue("instagramID", "");
              setValue("useFBPageAsIG", false);
              setValue("catalogID", "");
            }}
            fullWidth={true}
          />
          <SelectPageFacebook
            variant="filled"
            control={control}
            integrationID={integrationID}
            adAccountID={adAccountID}
            name="pageID"
            onChangeAction={() => {
              setValue("useFBPageAsIG", false);
              setValue("instagramID", "");
            }}
            label={t(PAGE_FACEBOOK_PLACEHOLDER)}
            fullWidth={true}
            sxTextField={{
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          />
          <SelectInstagramAccount
            variant="filled"
            control={control}
            integrationID={integrationID}
            adAccountID={adAccountID}
            pageID={pageID}
            name="instagramID"
            nameUseFBPageAsIG="useFBPageAsIG"
            label={instagramLabel}
            fullWidth={true}
            isInstagramRequired={isInstagramRequired}
            sxTextField={{
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          />
        </Box>
      ) : null}

      {visibility.catalogID ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(catalogTitleKey)}
          </Typography>
          <SelectCatalogMeta
            control={control}
            name="catalogID"
            label={t(CATALOG_PLACEHOLDER)}
            fullWidth
            variant="filled"
            platform={META}
            integrationID={integrationID}
            adAccountID={adAccountID}
          />
        </Box>
      ) : null}

      {visibility.appID ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(CONNECTED_ACCOUNTS_PLATFORM_DEFAULTS_APP_LABEL)}
          </Typography>
          <SelectAdvertisableAppsMeta
            placeholder={t(SELECT_A_OPTION)}
            platform={META}
            adAccountID={adAccountID}
            integrationID={integrationID}
            name="appID"
            fullWidth
            variant="filled"
            control={control}
          />
        </Box>
      ) : null}

      {visibility.trackingID ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2" sx={PLATFORM_DEFAULTS_FIELD_LABEL_SX}>
            {t(TRACKING_META_ID_PLACEHOLDER)}
          </Typography>
          <SelectTrackingIDField
            control={control}
            name="trackingID"
            label={t(SELECT_A_OPTION)}
            fullWidth
            variant="filled"
            platform={META}
            integrationID={integrationID}
            adAccountID={adAccountID}
            rules={{}}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default PlatformDefaultsFormMeta;
